import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { socialLogin } from '../../../store/actions/AuthActions';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const APPLE_SDK = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

const loadAppleSdk = () =>
  new Promise((resolve, reject) => {
    if (window.AppleID) return resolve(window.AppleID);
    const existing = document.querySelector(`script[src="${APPLE_SDK}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.AppleID));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = APPLE_SDK;
    s.async = true;
    s.onload = () => resolve(window.AppleID);
    s.onerror = reject;
    document.head.appendChild(s);
  });

/**
 * Google + Apple sign-in buttons. Renders nothing until it knows a provider
 * is configured. `onResult({ success, hasSubscription, error })` lets the
 * host page handle navigation exactly like its password flow.
 */
const SocialAuth = ({ onResult }) => {
  const dispatch = useDispatch();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/settings/social-public`)
      .then((r) => setCfg(r.data))
      .catch(() => setCfg({ enable_social_login: false }));
  }, []);

  const finish = useCallback((result) => {
    setBusy(false);
    if (!result.success) setError(result.error || 'Sign-in failed');
    if (onResult) onResult(result);
  }, [onResult]);

  const handleGoogle = async (credentialResponse) => {
    if (!credentialResponse?.credential) return;
    setError('');
    setBusy(true);
    finish(await dispatch(socialLogin('google', credentialResponse.credential)));
  };

  const handleApple = async () => {
    setError('');
    setBusy(true);
    try {
      const AppleID = await loadAppleSdk();
      AppleID.auth.init({
        clientId: cfg.apple_client_id,
        scope: 'name email',
        redirectURI: cfg.apple_redirect_uri || `${window.location.origin}/login`,
        usePopup: true,
      });
      const data = await AppleID.auth.signIn();
      const token = data?.authorization?.id_token;
      if (!token) throw new Error('No Apple token returned');
      finish(await dispatch(socialLogin('apple', token, { name: data.user?.name })));
    } catch (e) {
      // user closed the popup, etc.
      if (e?.error !== 'popup_closed_by_user') setError('Apple sign-in failed');
      setBusy(false);
    }
  };

  if (!cfg || !cfg.enable_social_login) return null;
  const showGoogle = !!cfg.google_client_id;
  const showApple = !!cfg.apple_client_id;
  if (!showGoogle && !showApple) return null;

  return (
    <div className="tw-mt-6">
      <div className="tw-mb-4 tw-flex tw-items-center tw-gap-3 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-content-faint">
        <span className="tw-h-px tw-flex-1 tw-bg-line" /> or continue with <span className="tw-h-px tw-flex-1 tw-bg-line" />
      </div>

      {error && (
        <div className="tw-mb-3 tw-rounded-lg tw-border tw-border-live/30 tw-bg-live/10 tw-px-3 tw-py-2 tw-text-sm tw-text-live">
          {error}
        </div>
      )}

      <div className={`tw-grid tw-gap-3 ${showGoogle && showApple ? 'sm:tw-grid-cols-2' : 'tw-grid-cols-1'}`}>
        {showGoogle && (
          <div className={`tw-flex tw-justify-center ${busy ? 'tw-pointer-events-none tw-opacity-60' : ''}`}>
            <GoogleOAuthProvider clientId={cfg.google_client_id}>
              <GoogleLogin
                onSuccess={handleGoogle}
                onError={() => setError('Google sign-in failed')}
                theme="outline"
                size="large"
                width="240"
                text="continue_with"
              />
            </GoogleOAuthProvider>
          </div>
        )}

        {showApple && (
          <button
            type="button"
            onClick={handleApple}
            disabled={busy}
            className="tw-flex tw-h-[40px] tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-bg-black tw-px-4 tw-text-sm tw-font-semibold tw-text-white tw-transition hover:tw-opacity-90 disabled:tw-opacity-60"
          >
            <i className="fa-brands fa-apple tw-text-base" /> Continue with Apple
          </button>
        )}
      </div>
    </div>
  );
};

export default SocialAuth;
